import { get, startCase } from "lodash";
import { DateTime } from "luxon";
import { Badge } from "@shared/ui";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";

import { ClientContext } from "../App";
import { getEnrichedPolicies } from "../lib/automatic";
import { sortPolicies } from "../lib/utility";
import LoadingSpinner from "./LoadingSpinner";
import Table from "./table/Table";
import { User } from "./table/User";

const MonitoringPoliciesTable = () => {
  const { selectedClient: clientId } = useContext(ClientContext);
  const { promiseInProgress } = usePromiseTracker();
  const [userPolicies, setUserPolicies] = useState(/** @type {any[]} */ ([]));
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);

  // Calculate validation result percentages for quick filters
  const calculateValidationPercentage = (validationStatus) => {
    if (!userPolicies.length) return "0%";

    const filteredPolicies = userPolicies.filter(
      (policy) => get(policy, "validationResults.status") === validationStatus
    );

    const percentage = (filteredPolicies.length / userPolicies.length) * 100;
    return `${Math.round(percentage)}%`;
  };

  const quickFilters = [
    {
      id: 1,
      label: "All policies",
      subLabel: isLoadingComplete
        ? `${userPolicies.length} total policies`
        : `${userPolicies.length} policies loaded...`,
      column: "validationResults.status",
      value: null,
    },
    {
      id: 2,
      label: "Passed validation",
      subLabel: isLoadingComplete
        ? `${calculateValidationPercentage("pass")} of policies passed`
        : "Calculating...",
      column: "validationResults.status",
      value: "pass",
    },
    {
      id: 3,
      label: "Failed validation",
      subLabel: isLoadingComplete
        ? `${calculateValidationPercentage("fail")} of policies failed`
        : "Calculating...",
      column: "validationResults.status",
      value: "fail",
    },
    {
      id: 4,
      label: "Caution required",
      subLabel: isLoadingComplete
        ? `${calculateValidationPercentage("caution")} of policies need review`
        : "Calculating...",
      column: "validationResults.status",
      value: "caution",
    },
  ];

  useEffect(() => {
    if (!clientId) return;

    // Reset state for new data fetch
    setUserPolicies([]);
    setIsLoadingComplete(false);

    trackPromise(
      getEnrichedPolicies(
        clientId,
        // Streaming callback - called for each batch of policies
        (newPolicies, allPoliciesSoFar, hasMorePages) => {
          // Sort policies in real-time as they arrive to maintain proper chronological order
          const sortedPoliciesSoFar = sortPolicies(allPoliciesSoFar);
          setUserPolicies(sortedPoliciesSoFar);
        }
      )
        .then((result) => {
          // Final result when all policies are loaded
          if (result && result.policies) {
            // Policies are already sorted from streaming callback, just mark as complete
            setUserPolicies(result.policies);
            setIsLoadingComplete(true);
          }
        })
        .catch((error) => {
          console.error("Error fetching enriched policies:", error);
          setUserPolicies([]);
          setIsLoadingComplete(true); // Mark as complete even on error
        })
    );
  }, [clientId]);

  const columns = useMemo(
    () => [
      {
        Header: "Policy Status",
        accessor: "isActive",
        Cell: ({ value }) => {
          if (value === true) {
            return <Badge status="active" />;
          } else if (value === false) {
            return <Badge status="inactive" />;
          } else if (value === undefined || value === null) {
            return <Badge status="unknown" />;
          }
        },
        width: 100,
      },
      {
        Header: "User",
        accessor: (row) => {
          const user = get(row, "user");
          if (get(user, "firstName") && get(user, "email")) {
            return JSON.stringify({
              id: get(user, "id"),
              firstName: get(user, "firstName"),
              lastName: get(user, "lastName"),
              email: get(user, "email"),
            });
          } else if (user) {
            return JSON.stringify({
              id: get(user, "id"),
              firstName: null,
              lastName: null,
              email: null,
            });
          } else {
            return JSON.stringify({
              id: null,
              firstName: null,
              lastName: null,
              email: null,
            });
          }
        },
        id: "user",
        Cell: ({ value }) => {
          const user = JSON.parse(value);
          if (user.id && user.firstName && user.email) {
            return <User value={value} />;
          } else if (user.id) {
            return user.id;
          } else {
            return "—";
          }
        },
        width: 100,
        isObject: true,
      },
      {
        Header: "Validation Result",
        accessor: "validationResults.status",
        id: "validationResults.status",
        Cell: ({ value }) => {
          if (value === "pass" || value === "fail" || value === "caution") {
            return <Badge status={value} />;
          } else {
            return value ? startCase(value) : "—";
          }
        },
        width: 100,
      },
      {
        Header: "Carrier",
        accessor: "carrier",
        Cell: ({ value }) => {
          return startCase(value);
        },
        width: 100,
      },
      {
        Header: "Connection",
        id: "connectionStatus",
        accessor: (row) => {
          let connectionStatus = get(row, "connectionStatus");
          if (connectionStatus === "inactive") {
            connectionStatus = "connection-inactive";
          }
          const refreshedAt = get(row, "refreshedAt");
          return {
            status: connectionStatus,
            refreshedAt: refreshedAt
              ? DateTime.fromISO(refreshedAt).toLocaleString({
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })
              : "—",
          };
        },
        Cell: ({ value }) => {
          return (
            <div className="flex flex-col">
              <div className="mb-2">{<Badge status={value.status} />}</div>
              <p className="text-gray-300">{value.refreshedAt}</p>
            </div>
          );
        },
        width: 75,
        isObject: true,
      },
      {
        accessor: "id",
        show: false,
        className: "hidden",
      },
    ],
    []
  );

  return (
    <>
      {promiseInProgress && userPolicies.length === 0 ? (
        <LoadingSpinner />
      ) : (
        <Table
          entity={"users"}
          data={userPolicies}
          columns={columns}
          rowBehavior={{
            type: "link",
            config: { path: "/policies", resourceKey: "id" },
          }}
          globalFilterPlaceholder={
            "Enter any user, status, result, or carrier..."
          }
          onRowClick={undefined}
          quickFilters={quickFilters}
          csvFields={[
            "id",
            "isActive",
            "user",
            "validationResults.status",
            "carrier",
            "connectionStatus",
            "refreshedAt",
          ]}
          dateFilter={null}
        />
      )}
    </>
  );
};

export default MonitoringPoliciesTable;
